// external
import type { JSX } from 'react';

// interstate ui components
import { Modal } from '@interstate/components/Modal';
import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { Grid } from '@interstate/components/Grid';
import { Button } from '@interstate/components/Button';
import type { AlertSeverity } from '@interstate/components/Alert/Types/alertTypes';

// types
import type { PushToFIProvider } from '@makemydeal/dr-dash-types';

// shared style with the RouteOne modal footer (same visual treatment)
import { StyledPushToRouteOneFooter } from '../pushToRouteOne/PushToRouteOneDialog.style';

const WAITING_MESSAGE = 'This will take a moment.';
const SLOW_MESSAGE = 'Your request is taking longer than expected. You can wait or cancel and try again later.';
const SAVE_OFFER_ERROR_SUB_MESSAGE = 'This deal encountered a problem and could not be saved. Please try again shortly';
const SUPPORT_HEADING = 'Please contact support:';
const SUPPORT_DETAIL = 'Call 1-888-895-2994 or create a support ticket';

export interface IFIModalLoadingProps {
    provider: PushToFIProvider;
    onHide(): void;
    header: JSX.Element;
    success?: boolean;
    slow?: boolean;
    error?: boolean;
    saveOfferError?: boolean;
}

type ModalVariant = 'waiting' | 'slow' | 'success' | 'offerError' | 'error';

const variantOf = (props: IFIModalLoadingProps): ModalVariant => {
    if (props.slow) return 'slow';
    if (props.success) return 'success';
    if (props.saveOfferError) return 'offerError';
    if (props.error) return 'error';
    return 'waiting';
};

export const FIModalLoading = (props: IFIModalLoadingProps): JSX.Element => {
    const { provider, onHide, header, success, error, saveOfferError } = props;

    const prefixBase = `push-to-fi-modal-${provider.targetPlatformId.toLowerCase()}`;
    const variant = variantOf(props);

    const prefix = (() => {
        switch (variant) {
            case 'slow':
                return `${prefixBase}-slow-modal`;
            case 'success':
                return `${prefixBase}-success-modal`;
            case 'offerError':
                return `${prefixBase}-offer-error-modal`;
            case 'error':
                return `${prefixBase}-error-modal`;
            default:
                return prefixBase;
        }
    })();

    const message = (() => {
        switch (variant) {
            case 'slow':
                return SLOW_MESSAGE;
            case 'success':
                return provider.modalCopy.successMessage;
            case 'offerError':
            case 'error':
                return provider.modalCopy.failureMessage;
            default:
                return WAITING_MESSAGE;
        }
    })();

    const infoType: AlertSeverity =
        variant === 'success'
            ? 'success'
            : variant === 'offerError' || variant === 'error'
            ? 'error'
            : variant === 'slow'
            ? 'caution'
            : 'info';

    const showCloseButton = { '#ids-modal-close-button': { display: 'content' } };
    const hideCloseButton = { '#ids-modal-close-button': { display: 'none' } };
    const sx = variant === 'waiting' ? hideCloseButton : showCloseButton;
    const isDismissDisabled = variant === 'waiting';

    const cancelFooter = (
        <Grid container justifyContent="flex-end" data-testid={`${prefix}-footer`}>
            <Button data-testid={`${prefix}-cancel-button`} buttonStyle="secondary" onClick={onHide}>
                Cancel
            </Button>
        </Grid>
    );
    const closeFooter = (
        <Grid container justifyContent="flex-end" data-testid={`${prefix}-footer`}>
            <Button data-testid={`${prefix}-close-button`} buttonStyle="primary" onClick={onHide}>
                Close
            </Button>
        </Grid>
    );

    const footer = variant === 'success' ? closeFooter : variant === 'waiting' ? undefined : cancelFooter;

    const noOutOfBoundsClicking = isDismissDisabled ? () => undefined : onHide;

    return (
        <Modal
            data-testid={prefix}
            size="large"
            show={true}
            header={header}
            onHide={noOutOfBoundsClicking}
            sx={sx}
            footer={{
                footerComponent: <StyledPushToRouteOneFooter>{footer}</StyledPushToRouteOneFooter>
            }}
        >
            {variant === 'success' && (
                <Alert type={infoType} disableSuccessFade={true}>
                    {message}
                </Alert>
            )}

            {(variant === 'error' || variant === 'offerError') && (
                <>
                    <Alert title={message} type={infoType} disableSuccessFade={true}>
                        {saveOfferError ? (
                            <span>{SAVE_OFFER_ERROR_SUB_MESSAGE}</span>
                        ) : (
                            <span>{provider.modalCopy.failureSubMessage}</span>
                        )}
                    </Alert>
                    <Typography variant="h5" sx={{ paddingTop: '1rem' }}>
                        {SUPPORT_HEADING}
                    </Typography>
                    <Typography variant="body-md">{SUPPORT_DETAIL}</Typography>
                </>
            )}

            {!success && !error && !saveOfferError && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        height: '10rem',
                        minHeight: '10rem'
                    }}
                >
                    <Spinner />
                    <Typography variant="body-md" sx={{ paddingTop: '1rem' }}>
                        {message}
                    </Typography>
                </div>
            )}
        </Modal>
    );
};

export default FIModalLoading;
